/**
 * The org setting "Allow noBGP support" and the temporary support grant (app#376, router
 * `org_support_access`, router#1772; docs/PERMISSIONS.md § 8, decided F-11).
 *
 * "Allow noBGP support" is ON by default. When it is off, noBGP staff cannot act in the org, and an
 * org Owner or Admin can give staff temporary access for a number of hours and end it early.
 * Reading needs membership; a change needs an Owner or Admin (`support.manage`).
 *
 * Pure, so `supportAccess.test.ts` checks it with `node --experimental-strip-types`.
 */

/** The `org_support_access` payload (`callTool` has already unwrapped it). */
export interface SupportAccessPayload {
  org_id?: string
  allow_support?: boolean
  grant_until?: string | null
  granted_by?: string | null
  staff_access?: boolean
}

/** The same, as the app holds it. */
export interface SupportAccess {
  allowSupport: boolean
  /** The end of a LIVE grant, or null. The router never returns a grant whose end has passed. */
  grantUntil: string | null
  grantedBy: string | null
  /** What every staff door answers now: the setting is on, or a grant is live. */
  staffAccess: boolean
}

/**
 * The payload as the app holds it, or null when it is not one.
 * ⚠ `allow_support` must be a boolean. A missing one is not "off": it is an answer this app cannot
 * read, and a page that showed it as off would tell the reader staff are locked out when they are
 * not.
 */
export function supportAccessFrom(payload: unknown): SupportAccess | null {
  const p = payload as SupportAccessPayload | null | undefined
  if (!p || typeof p.allow_support !== 'boolean') return null
  const grantUntil = typeof p.grant_until === 'string' && p.grant_until ? p.grant_until : null
  return {
    allowSupport: p.allow_support,
    grantUntil,
    grantedBy: typeof p.granted_by === 'string' && p.granted_by ? p.granted_by : null,
    staffAccess: typeof p.staff_access === 'boolean' ? p.staff_access : p.allow_support || !!grantUntil,
  }
}

/** `org_support_access` arguments for each op. */
export const supportReadArgs = (orgId: string) => ({ org_id: orgId, op: 'get' })
export const supportSettingArgs = (orgId: string, allowSupport: boolean) => ({
  org_id: orgId,
  op: 'set',
  allow_support: allowSupport,
})
export const supportGrantArgs = (orgId: string, hours: number) => ({ org_id: orgId, op: 'grant', hours })
export const supportEndArgs = (orgId: string) => ({ org_id: orgId, op: 'end' })

/** The grant lengths the page offers, in hours. The router takes 1 to 720. */
export const GRANT_HOURS: readonly { hours: number; label: string }[] = [
  { hours: 1, label: '1 hour' },
  { hours: 4, label: '4 hours' },
  { hours: 24, label: '24 hours' },
  { hours: 72, label: '3 days' },
  { hours: 168, label: '7 days' },
]

/** Whether `hours` is a grant length the router takes. */
export const validGrantHours = (hours: number): boolean => Number.isInteger(hours) && hours >= 1 && hours <= 720

/**
 * One line that says whether noBGP support may act in the org now, and why.
 * ⚠ A grant whose end is already past (a page left open) reads as no grant, so the line never says
 * staff have access they no longer have.
 */
export function supportStatusLine(access: SupportAccess, now: number, formatTime: (iso: string) => string): string {
  const live = !!access.grantUntil && new Date(access.grantUntil).getTime() > now
  if (access.allowSupport) {
    return 'noBGP support may help in this organization. Every action they take is recorded in the audit log.'
  }
  if (live && access.grantUntil) {
    return `noBGP support has temporary access until ${formatTime(access.grantUntil)}. Every action they take is recorded in the audit log.`
  }
  return 'noBGP support cannot act in this organization. Give temporary access when you need their help.'
}

/** A router tool error as `callTool` throws it, read by shape. */
type ToolFailure = { code?: string; message?: string } | null | undefined

/** The sentence a reader who may not change the setting gets. */
export const SUPPORT_FORBIDDEN = 'Only an owner or admin of this organization can change noBGP support access.'

/** One sentence for a refused change. */
export function supportRefusal(error: unknown): string {
  const failure = error as ToolFailure
  if (failure?.code === 'forbidden') return SUPPORT_FORBIDDEN
  if (failure?.code === 'unauthenticated' || failure?.code === 'unauthorized') {
    return 'Your session has ended. Sign in again, then try once more.'
  }
  return failure?.message || 'noBGP support access was not changed.'
}
